// ABOUTME: Step one of applying (B1): the job's days against the talent's week, a day edited in place (B1a/B1b), weekend
// ABOUTME: and holidays, hours per week and the start, the result as pro-core reads it, and whether to save the changes.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { Text } from 'components/Text';
import { RangeListEditor, weekdayLabel } from 'modules/availability';
import type { TimeRange, Weekday } from 'modules/availability/types';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { Iconify } from 'modules/minimal-ui-theme';
import type {
  JobListing,
  WeekFitAnswer,
  WeekFitDay,
  WeekFitWarning
} from '../../types';
import { formatDay, hoursLabel } from '../../utils';
import { WeekEdits } from './weekDraft';

export interface WeekStepProps {
  listing: JobListing;
  answer: WeekFitAnswer | undefined;
  /** The answer for the profile's own week, before any edit: whether anything was prefilled. Defaults to answer. */
  profileAnswer?: WeekFitAnswer;
  /** Changing the week of an application already sent (C1): no "you can still apply". */
  applied?: boolean;
  isError: boolean;
  /** A changed week is being checked: the hints of the answer before it are left out until it is back. */
  isChecking?: boolean;
  edits: WeekEdits;
  editing: Weekday | null;
  onEdit: (day: Weekday | null) => void;
  onChangeDay: (day: Weekday, ranges: TimeRange[]) => void;
  weeklyMinutes: number;
  onWeeklyMinutes: (minutes: number) => void;
  startFrom: string;
  onStartFrom: (date: string) => void;
  holidaysOk: boolean;
  onHolidaysOk: (ok: boolean) => void;
  saveToProfile: boolean;
  onSaveToProfile: (save: boolean) => void;
}

const range = (ranges: TimeRange[]): string =>
  ranges.map(({ start, end }) => `${start}–${end}`).join(', ');

/** One of the job's days: the talent's time, a tick when it is enough, „Zeit angeben“ when not; tap to edit. */
const DayRow = ({
  day,
  ranges,
  changed,
  onEdit
}: {
  day: WeekFitDay;
  ranges: TimeRange[];
  changed: boolean;
  onEdit: () => void;
}) => (
  <ButtonBase
    onClick={onEdit}
    data-testid="apply-week-day"
    data-day={day.dayOfWeek}
    data-fits={day.fits}
    sx={{
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1.5,
      py: 1.25,
      borderTop: '1px solid',
      borderColor: 'divider',
      textAlign: 'left'
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Text variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
        {weekdayLabel(day.dayOfWeek)}
        {changed && (
          <Text
            component="span"
            variant="caption"
            color="primary.main"
            sx={{ ml: 1, fontWeight: 700 }}
          >
            {getTrans('jobs.apply.week.changed')}
          </Text>
        )}
      </Text>
      <Text variant="caption" color="text.secondary" component="p">
        {ranges.length ? range(ranges) : getTrans('jobs.apply.week.noTime')}
      </Text>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {day.fits ? (
        <Text variant="caption" color="success.dark" sx={{ fontWeight: 700 }}>
          {getTrans('jobs.apply.week.fits')}
        </Text>
      ) : (
        <Text variant="caption" color="text.secondary">
          {getTrans('jobs.apply.week.addTime')}
        </Text>
      )}
      <Iconify icon="eva:arrow-ios-forward-fill" width={16} />
    </Box>
  </ButtonBase>
);

/** B1a: one day's times, with what the job needs that day. */
const DayEditor = ({
  day,
  ranges,
  onChange,
  onDone
}: {
  day: WeekFitDay;
  ranges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
  onDone: () => void;
}) => (
  <Box data-testid="apply-week-day-editor" sx={{ display: 'grid', gap: 1.5 }}>
    <Text variant="subtitle1" color="text.primary" sx={{ fontWeight: 700 }}>
      {getTransWithParams('jobs.apply.week.editTitle', {
        label: weekdayLabel(day.dayOfWeek)
      })}
    </Text>
    {(day.needed.window || !!day.needed.minMinutes) && (
      <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
        {day.needed.window
          ? getTransWithParams('jobs.apply.week.needWindow', {
              from: day.needed.window.start,
              to: day.needed.window.end
            })
          : getTransWithParams('jobs.apply.week.needMinimum', {
              hours: String(day.needed.minMinutes! / 60)
            })}
      </Alert>
    )}
    <RangeListEditor
      label={getTrans('availabilityPage.editor.availableLabel')}
      ranges={ranges}
      onChange={onChange}
      addLabel={getTrans('availabilityPage.editor.addHours')}
      dataTestId="apply-week-ranges"
    />
    <ButtonBase
      onClick={onDone}
      data-testid="apply-week-day-done"
      sx={{
        justifySelf: 'start',
        fontWeight: 700,
        color: 'primary.main',
        fontSize: 14
      }}
    >
      {getTrans('jobs.apply.week.editDone')}
    </ButtonBase>
  </Box>
);

const RESULT_SEVERITY = {
  FITS: 'success',
  PARTLY: 'warning',
  NO: 'warning'
} as const;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A warning's hint, with the job's figure and the talent's: "Der Job braucht mind. 20 Std. pro Woche, du hast 16 angegeben." */
const warningText = (
  warning: WeekFitWarning,
  listing: JobListing,
  weeklyMinutes: number,
  startFrom: string
): string | undefined => {
  if (warning === 'WEEKLY_HOURS') {
    if (!listing.minWeeklyHourMinutes) return undefined;
    return getTransWithParams('jobs.apply.week.warning.WEEKLY_HOURS', {
      hours: hoursLabel(
        listing.minWeeklyHourMinutes,
        listing.minWeeklyHourMinutes
      ),
      yours: hoursLabel(weeklyMinutes, weeklyMinutes)
    });
  }
  // A cleared date field has no day to name (the answer on screen may still be the one before), nor has a job without a
  // start. As pro-core, a job that has started is compared from today, so the hint names today, never a past day.
  if (!ISO_DAY.test(startFrom) || !ISO_DAY.test(listing.startDate ?? '')) {
    return undefined;
  }
  const today = new Date().toLocaleDateString('sv-SE', {
    timeZone: 'Europe/Berlin'
  });
  return getTransWithParams('jobs.apply.week.warning.START_DATE', {
    yours: formatDay(startFrom),
    job: formatDay(listing.startDate < today ? today : listing.startDate)
  });
};

/** "Der Job: Mo, Do nach Absprache", with the daily minimum only where the job has one. */
const byArrangementLabel = (listing: JobListing): string => {
  const label = (listing.workingDays ?? [])
    .map(day => getTrans(`jobs.filters.day.${day}` as TranslationKey))
    .join(', ');
  return listing.minHoursPerDayMinutes
    ? getTransWithParams('jobs.apply.week.needByArrangement', {
        label,
        hours: String(listing.minHoursPerDayMinutes / 60)
      })
    : getTransWithParams('jobs.apply.week.needByArrangementAnyHours', {
        label
      });
};

/**
 * The line on top. "Vorausgefüllt" only when the profile had times on the job's days; no week yet asks for one; times
 * entered here without a saved week, or a profile not read, get the neutral line about where the times go.
 */
const introKey = (
  profileDays: WeekFitDay[],
  edited: boolean
): TranslationKey => {
  if (profileDays.some(day => day.yours.length > 0)) {
    return 'jobs.apply.week.prefilled';
  }
  return profileDays.length && !edited
    ? 'jobs.apply.week.empty'
    : 'jobs.apply.week.entered';
};

export const WeekStep = ({
  listing,
  answer,
  profileAnswer,
  applied = false,
  isError,
  isChecking = false,
  edits,
  editing,
  onEdit,
  onChangeDay,
  weeklyMinutes,
  onWeeklyMinutes,
  startFrom,
  onStartFrom,
  holidaysOk,
  onHolidaysOk,
  saveToProfile,
  onSaveToProfile
}: WeekStepProps) => {
  const days = answer?.days ?? [];
  const rangesOf = (day: WeekFitDay) => edits[day.dayOfWeek] ?? day.yours;
  const editingDay = days.find(day => day.dayOfWeek === editing);
  const fitting = days.filter(day => day.fits).length;
  const asksHolidays =
    answer?.weekend?.holidays === 'OCCASIONAL' ||
    answer?.weekend?.holidays === 'REGULAR';

  if (editingDay) {
    return (
      <DayEditor
        day={editingDay}
        ranges={rangesOf(editingDay)}
        onChange={ranges => onChangeDay(editingDay.dayOfWeek, ranges)}
        onDone={() => onEdit(null)}
      />
    );
  }

  return (
    <Box data-testid="apply-week-step" sx={{ display: 'grid', gap: 2 }}>
      <Text variant="body2" color="text.secondary" component="p">
        {getTrans(
          introKey(
            (profileAnswer ?? answer)?.days ?? [],
            Object.keys(edits).length > 0
          )
        )}
      </Text>
      {isError && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {getTrans('jobs.apply.week.error')}
        </Alert>
      )}

      <Box>
        <Text
          variant="subtitle1"
          component="h3"
          color="primary.main"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          {getTrans('jobs.apply.week.daysTitle')}
        </Text>
        <Alert severity="info" icon={false} sx={{ borderRadius: 2, mb: 1 }}>
          {listing.workingTimeMode === 'FIXED'
            ? getTrans('jobs.apply.week.needFixed')
            : byArrangementLabel(listing)}
        </Alert>
        {days.map(day => (
          <DayRow
            key={day.dayOfWeek}
            day={day}
            ranges={rangesOf(day)}
            changed={!!edits[day.dayOfWeek]}
            onEdit={() => onEdit(day.dayOfWeek)}
          />
        ))}
      </Box>

      {asksHolidays && (
        <FormControlLabel
          control={
            <Switch
              checked={holidaysOk}
              onChange={event => onHolidaysOk(event.target.checked)}
              inputProps={{
                'aria-label': getTrans('jobs.apply.week.holidays')
              }}
            />
          }
          label={getTrans('jobs.apply.week.holidays')}
        />
      )}

      <Box>
        <Text
          variant="subtitle1"
          component="h3"
          color="primary.main"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          {getTrans('jobs.apply.week.hours')}
        </Text>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            aria-label={getTrans('jobs.apply.week.less')}
            onClick={() => onWeeklyMinutes(Math.max(60, weeklyMinutes - 60))}
            data-testid="apply-week-hours-less"
          >
            <Iconify icon="eva:minus-fill" />
          </IconButton>
          <Text
            data-testid="apply-week-hours"
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 700, flex: 1, textAlign: 'center' }}
          >
            {getTransWithParams('jobs.card.hoursPerWeek', {
              hours: hoursLabel(weeklyMinutes, weeklyMinutes)
            })}
          </Text>
          <IconButton
            aria-label={getTrans('jobs.apply.week.more')}
            onClick={() =>
              onWeeklyMinutes(Math.min(40 * 60, weeklyMinutes + 60))
            }
            data-testid="apply-week-hours-more"
          >
            <Iconify icon="eva:plus-fill" />
          </IconButton>
        </Box>
        <Text variant="caption" color="text.secondary" component="p">
          {getTransWithParams('jobs.apply.week.hoursHint', {
            label: hoursLabel(
              listing.minWeeklyHourMinutes,
              listing.maxWeeklyHourMinutes
            )
          })}
        </Text>
      </Box>

      <TextField
        type="date"
        label={getTrans('jobs.apply.week.start')}
        value={startFrom}
        onChange={event => onStartFrom(event.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'data-testid': 'apply-week-start' }}
      />

      {answer && (
        <Alert
          severity={RESULT_SEVERITY[answer.result]}
          data-testid="apply-week-result"
          data-result={answer.result}
          sx={{ borderRadius: 2 }}
        >
          <Text variant="body2" sx={{ fontWeight: 700 }}>
            {getTrans(
              `jobs.apply.week.result.${answer.result}` as TranslationKey
            )}
          </Text>
          <Text variant="body2">
            {getTransWithParams('jobs.apply.week.result.days', {
              done: fitting,
              total: days.length
            })}
          </Text>
          {answer.result !== 'FITS' && (
            <Text variant="caption" component="p">
              {getTrans(
                applied
                  ? 'jobs.apply.week.result.warnApplied'
                  : 'jobs.apply.week.result.warn'
              )}
            </Text>
          )}
        </Alert>
      )}

      {/* Gentle hints from pro-core, never a block: applying stays open whatever they say. */}
      {(isChecking ? [] : answer?.warnings ?? []).map(warning => {
        const text = warningText(warning, listing, weeklyMinutes, startFrom);
        return (
          text && (
            <Alert
              key={warning}
              severity="info"
              data-testid="apply-week-warning"
              data-warning={warning}
              sx={{ borderRadius: 2 }}
            >
              {text}
            </Alert>
          )
        );
      })}

      {Object.keys(edits).length > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={saveToProfile}
              onChange={event => onSaveToProfile(event.target.checked)}
              inputProps={{
                'aria-label': getTrans('jobs.apply.week.saveToProfile')
              }}
            />
          }
          label={getTrans('jobs.apply.week.saveToProfile')}
        />
      )}
    </Box>
  );
};
